import { CareProtocolApi } from "@/api/careProtocol/CareProtocolApi";
import { supabase } from "@/api/supabaseClient";
import { Platform } from "react-native";

export type PickedProtocolImage = { uri: string; fileName?: string | null };

function newClientMediaId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function readBody(
  uri: string,
  mimeType: string
): Promise<Blob | ArrayBuffer> {
  if (Platform.OS === "web") {
    const res = await fetch(uri);
    if (!res.ok) throw new Error("Datei konnte nicht gelesen werden");
    return await res.blob();
  }
  const FileSystem = await import("expo-file-system/legacy");
  const base64 = await FileSystem.readAsStringAsync(uri, {
    encoding: FileSystem.EncodingType.Base64,
  });
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Zwei-Phasen-Upload für Verpackungsfotos (privater Bucket care-protocol-media).
 * Liefert media_asset_id für packaging_photo_media_id im submit_care_entry.
 */
export async function uploadPackagingPhoto(input: {
  caseId: string;
  image: PickedProtocolImage;
}): Promise<string> {
  const ext = (
    (input.image.fileName || input.image.uri).split(".").pop() ?? "jpg"
  )
    .split("?")[0]
    .toLowerCase();
  const safeExt = ["jpg", "jpeg", "png", "webp"].includes(ext) ? ext : "jpg";
  const contentType =
    safeExt === "png"
      ? "image/png"
      : safeExt === "webp"
        ? "image/webp"
        : "image/jpeg";

  const intent = (await CareProtocolApi.createMediaUploadIntent({
    caseId: input.caseId,
    clientMediaId: newClientMediaId(),
    contentType,
  })) as { id: string; storage_path: string };

  if (!intent?.id || !intent?.storage_path) {
    throw new Error("Upload-Intent fehlgeschlagen.");
  }

  const body = await readBody(input.image.uri, contentType);
  const { error } = await supabase.storage
    .from("care-protocol-media")
    .upload(intent.storage_path, body, {
      upsert: false,
      contentType,
    });
  if (error) {
    throw new Error(error.message || "Verpackungsfoto-Upload fehlgeschlagen.");
  }
  return intent.id;
}
